export interface ZoomParseResult {
  value: number;
  clamped: boolean;
  /** The value before clamping, for hint display. */
  raw: number;
  /** How the input was interpreted: explicit suffix or bare-number heuristic. */
  scale: 'percent' | 'multiplier';
}

const NUM_RE = /^\d+(?:\.\d+)?$/;
/** Bare numbers strictly below 5 are interpreted as multipliers (`2.3` → 2.3×).
 *  At 5 and above they're percent (`5` → 5%, `150` → 150%). Mirrors the
 *  /zoom command's UX: a small fraction like 2.3 reads as zoom level, a
 *  larger integer like 150 reads as a percent. */
const BARE_PERCENT_THRESHOLD = 5;

export function parseZoomInput(input: string, range: [number, number]): ZoomParseResult | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  let body = trimmed;
  let scale: 'percent' | 'multiplier' | 'auto' = 'auto';

  if (body.endsWith('%')) {
    body = body.slice(0, -1).trim();
    scale = 'percent';
  } else if (body.endsWith('x') || body.endsWith('X') || body.endsWith('×')) {
    body = body.slice(0, -1).trim();
    scale = 'multiplier';
  }

  if (!NUM_RE.test(body)) return null;
  const n = Number(body);
  if (!Number.isFinite(n) || n < 0) return null;

  let multiplier: number;
  let resolvedScale: 'percent' | 'multiplier';
  if (scale === 'percent') {
    multiplier = n / 100;
    resolvedScale = 'percent';
  } else if (scale === 'multiplier') {
    multiplier = n;
    resolvedScale = 'multiplier';
  } else if (n >= BARE_PERCENT_THRESHOLD) {
    multiplier = n / 100;
    resolvedScale = 'percent';
  } else {
    multiplier = n;
    resolvedScale = 'multiplier';
  }

  const [min, max] = range;
  const clamped = Math.max(min, Math.min(max, multiplier));
  return {
    value: clamped,
    clamped: clamped !== multiplier,
    raw: multiplier,
    scale: resolvedScale,
  };
}
